import { SkyButton, SkyMessageBar, SkyTabList, SkyText } from "@skypointcloud/skypoint-ui";
import type { TabItem } from "@skypointcloud/skypoint-ui";
import { useMemo, useState, type ReactElement } from "react";
import { ErrorState, LoadingRows, PermissionNotice } from "@/components/access/ScreenStates";
import { AuditEventDrawer } from "@/components/audit/AuditEventDrawer";
import { AuditIntegrityPanel, InstanceIntegrityLine } from "@/components/audit/AuditIntegrity";
import {
  AuditEventsGrid,
  NO_MATCHING_EVENTS,
  eventToRow,
  reportRowToRow,
  type AuditGridRow,
} from "@/components/audit/AuditEventsGrid";
import { AuditFilters } from "@/components/audit/AuditFilters";
import { AuditReportPicker } from "@/components/audit/AuditReportPicker";
import { TenantAuditTrail } from "@/components/audit/TenantAuditTrail";
import { TopbarActionsPortal } from "@/components/navigation/TopbarActionsPortal";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import { useNotice } from "@/lib/feedback/NoticeProvider";
import { useApiErrorNotice } from "@/lib/feedback/useApiErrorNotice";
import { usePermissions } from "@/hooks/use-permissions";
import {
  auditExportPath,
  useAuditEvents,
  useAuditExport,
  useAuditReport,
  useCursorStack,
  type AuditFilters as AuditFilterValues,
  type AuditScope,
  type AuditView,
} from "@/hooks/use-audit";
import { ChevronLeftIcon, ChevronRightIcon, DownloadIcon } from "@/lib/icons";
import type { AuditReportId } from "@shared/types/auditReports";
import { RefreshErrorBar } from "@/components/states/RefreshErrorBar";
import { useMcpEnabled } from "@/hooks/use-mcp";

/** What an empty trail with no filter set says, once (walk W19). */
const NOTHING_YET: Record<AuditScope, string> = {
  instance: "Nothing has happened in this instance yet.",
  platform: "Nothing has happened on this platform yet.",
};

/**
 * The audit trail (canvas 09; FR-70, SEC-16, SEC-17, SEC-18).
 *
 * Two things here are load-bearing rather than cosmetic.
 *
 * **Pagination walks a cursor, not page numbers.** The tables are append-only and
 * partitioned by month, so an offset would drift between two requests and a page
 * number would be a claim the data cannot keep. Previous is therefore a stack of
 * the cursors already visited.
 *
 * **The Platform tab appears only with `platform.audit.read`.** Instance rows live
 * in the instance's own tenant database and platform rows in the platform one, so
 * the two tabs are genuinely two trails rather than one list with a filter.
 *
 * **The tenant tab appears with `tenant.audit.read` (product gaps row 9).** It is the
 * selected instance's tenant, merged by the server from the platform rows about it and
 * every instance's trail; `TenantAuditTrail` owns its filters, grid and export.
 *
 * **The Integrity tab appears with `platform.audit.read`** (the HIPAA audit controls, 2026-09-29;
 * always on since #2501, owner 2026-11-02, no flag): one row per chain from the runner's daily verification, and
 * "Verify now". The instance tab carries one line with its own trail's result.
 */

/** The tab strip: the two trails `use-audit` reads, the tenant's merged one, and integrity. */
type AuditTab = AuditScope | "tenant" | "integrity";
/**
 * The trail the strip's tab asks for, clamped to what the user may read: the tenant and integrity
 * tabs sit over the instance trail, and a reader of only one trail gets that one.
 */
function clampedScope(
  requestedTab: AuditTab,
  canReadInstance: boolean,
  canReadPlatform: boolean,
): AuditScope {
  const requestedScope: AuditScope =
    requestedTab === "tenant" || requestedTab === "integrity" ? "instance" : requestedTab;
  if (requestedScope === "platform" && !canReadPlatform) return "instance";
  if (requestedScope === "instance" && !canReadInstance && canReadPlatform) return "platform";
  return requestedScope;
}

function shownTab(onIntegrityTab: boolean, onTenantTab: boolean, scope: AuditScope): string {
  if (onIntegrityTab) return "integrity";
  return onTenantTab ? "tenant" : scope;
}

/**
 * The line under the grid. The grid already says why it is empty (walk W19): this line says only
 * what to do about a filter, and nothing when no filter is set.
 */
function auditPageLine(count: number, trulyEmpty: boolean): string | null {
  if (count > 0) return `Showing ${count} events on this cursor page.`;
  return trulyEmpty ? null : "Widen the date range or clear a filter.";
}

/**
 * #1090: the denials report lists what the server refused. A screen the app closes for a role
 * before it asks the server is not a refusal and is not recorded, by design: the server's own
 * refusals are the evidence, and an event a browser sends could be forged and would flood the
 * trail. The banner says so, so a reviewer does not read the gap as missing evidence.
 */
const DENIALS_MESSAGE =
  "Requests the server refused, newest first. A screen the app closes for a person's role sends no request, so opening it is not recorded here. Only the server's own refusals are recorded, because a record sent from a browser could be forged.";

/**
 * #1090 review: a refusal on a route with no instance context (a tenant or platform page) is
 * written to the platform trail (`recordDenied`, server/middleware/errorHandler.ts), so the
 * instance's report says where it is.
 */
const DENIALS_OUTSIDE_INSTANCE =
  "A refusal outside this instance, such as on a tenant or platform page, is recorded in the platform audit trail.";

function bannerMessage(
  spec: NonNullable<ReturnType<typeof useAuditReport>["spec"]>,
  scope: AuditScope,
): string {
  if (spec.breakGlassFirst) {
    return "Break-glass grants are listed first, whatever their date, so a self-grant cannot be missed by a reviewer who reads only the first page. Everything else follows newest first.";
  }
  if (spec.id === "denials") {
    return scope === "instance"
      ? `${DENIALS_MESSAGE} ${DENIALS_OUTSIDE_INSTANCE}`
      : DENIALS_MESSAGE;
  }
  return "A fixed set of actions over the date range you choose, newest first.";
}

function ReportBanner({
  spec,
  scope,
}: {
  spec: NonNullable<ReturnType<typeof useAuditReport>["spec"]>;
  scope: AuditScope;
}) {
  return (
    <SkyMessageBar
      intent="info"
      layout="multiline"
      testId="report-banner"
      title={spec.label}
      message={bannerMessage(spec, scope)}
    />
  );
}

/**
 * What the reader may open: the instance and platform trails, the tenant's (it needs a selected
 * instance), and integrity (always on, #2501), with the strip's tabs in that order.
 */
function useAuditTabs(can: ReturnType<typeof usePermissions>["can"], hasInstance: boolean) {
  const canReadInstance = can("instance.audit.read");
  const canReadPlatform = can("platform.audit.read");
  const canReadTenant = can("tenant.audit.read") && hasInstance;
  const canSeeIntegrity = canReadPlatform;
  const tabs = useMemo<TabItem[]>(() => {
    const items: TabItem[] = [];
    if (canReadInstance) items.push({ value: "instance", label: "This instance" });
    if (canReadTenant) items.push({ value: "tenant", label: "This tenant" });
    if (canReadPlatform) items.push({ value: "platform", label: "Platform" });
    if (canSeeIntegrity) items.push({ value: "integrity", label: "Integrity" });
    return items;
  }, [canReadInstance, canReadTenant, canReadPlatform, canSeeIntegrity]);
  return { canReadInstance, canReadPlatform, canReadTenant, canSeeIntegrity, tabs };
}

/** The tab strip, only when there is more than one trail to choose. */
function AuditTabStrip({
  tabs,
  selectedValue,
  onSelect,
}: {
  tabs: TabItem[];
  selectedValue: string;
  onSelect: (tab: AuditTab) => void;
}) {
  if (tabs.length <= 1) return null;
  return (
    <SkyTabList
      tabs={tabs}
      selectedValue={selectedValue}
      testId="audit-scope-tabs"
      onTabSelect={(value) => onSelect(value as AuditTab)}
    />
  );
}

function ExportCsvButton({
  shown,
  exporting,
  onExport,
}: {
  shown: boolean;
  exporting: boolean;
  onExport: () => void;
}) {
  if (!shown) return null;
  return (
    <TopbarActionsPortal>
      <SkyButton
        appearance="secondary"
        icon={DownloadIcon}
        loading={exporting}
        testId="export-csv"
        onClick={onExport}
      >
        Export CSV
      </SkyButton>
    </TopbarActionsPortal>
  );
}

/** The tenant's merged trail; a new tenant starts a new trail (its filters and cursor never carry over). */
function tenantTrailFor(
  selectedInstance: ReturnType<typeof useTenantInstance>["selectedInstance"],
  instances: ReadonlyArray<{ id: string; name: string }>,
): ReactElement | null {
  if (!selectedInstance) return null;
  return (
    <TenantAuditTrail
      key={selectedInstance.tenantId}
      tenantId={selectedInstance.tenantId}
      tenantName={selectedInstance.tenantDisplayName}
      instances={instances}
    />
  );
}

/** Over the trail: the instance's integrity line, the report picker and banner, the filters. */
function TrailControls({
  scope,
  canReadInstance,
  view,
  spec,
  onViewChange,
  filters,
  onFiltersChange,
}: {
  scope: AuditScope;
  canReadInstance: boolean;
  view: AuditView;
  /** The chosen report's spec; the banner shows only while a report is chosen. */
  spec: ReturnType<typeof useAuditReport>["spec"] | undefined;
  onViewChange: (view: AuditView) => void;
  filters: AuditFilterValues;
  onFiltersChange: (filters: AuditFilterValues) => void;
}) {
  const isReport = view !== "all";
  const mcpEnabled = useMcpEnabled();
  return (
    <>
      {scope === "instance" ? <InstanceIntegrityLine enabled={canReadInstance} /> : null}
      <AuditReportPicker view={view} showLegend={isReport} onChange={onViewChange} />

      {isReport && spec ? <ReportBanner spec={spec} scope={scope} /> : null}

      <AuditFilters
        filters={filters}
        showActionFilters={!isReport}
        offerMcpActions={mcpEnabled}
        onChange={onFiltersChange}
        onClear={() => onFiltersChange({})}
      />
    </>
  );
}

/** The trail's read: loading, its failure with Retry, or the grid with its line and pager. */
function AuditResults({
  active,
  rows,
  selected,
  onOpen,
  emptyMessage,
  pageLine,
  canGoBack,
  onPrevious,
  onNext,
  onRetry,
}: {
  active: {
    isPending: boolean;
    isError: boolean;
    error: unknown;
    data?: unknown;
    nextCursor?: string | null;
  };
  rows: AuditGridRow[];
  selected: AuditGridRow | null;
  onOpen: (row: AuditGridRow) => void;
  emptyMessage: string;
  pageLine: string | null;
  canGoBack: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onRetry: () => void;
}) {
  if (active.isPending) return <LoadingRows rows={6} columns={6} />;
  // #1398: only a failed first read replaces the trail; a failed re-read keeps the rows.
  if (active.isError && active.data === undefined) {
    return (
      <ErrorState title="The audit trail did not load" error={active.error} onRetry={onRetry} />
    );
  }
  return (
    <>
      {active.isError ? (
        <RefreshErrorBar
          things="events"
          error={active.error}
          onRetry={onRetry}
          empty={rows.length === 0}
          testId="audit-refresh-error"
        />
      ) : null}
      <AuditEventsGrid
        rows={rows}
        selectedId={selected?.id ?? null}
        onOpen={onOpen}
        emptyMessage={emptyMessage}
      />
      <div className="flex items-center gap-3">
        {/* The grid already says why it is empty (walk W19): this line says only
            what to do about a filter, and nothing when no filter is set. */}
        <SkyText as="p" size={200} color="muted">
          {pageLine}
        </SkyText>
        <span className="ml-auto flex items-center gap-2">
          <SkyButton
            appearance="subtle"
            size="small"
            icon={ChevronLeftIcon}
            testId="audit-previous"
            disabled={!canGoBack}
            onClick={onPrevious}
          >
            Previous
          </SkyButton>
          <SkyButton
            appearance="secondary"
            size="small"
            icon={ChevronRightIcon}
            testId="audit-next"
            disabled={!active.nextCursor}
            onClick={onNext}
          >
            Next
          </SkyButton>
        </span>
      </div>
    </>
  );
}

export default function AdminAuditPage() {
  const { can, canAny, isResolved } = usePermissions();
  const { selectedInstance, me } = useTenantInstance();
  const notice = useNotice();
  const showError = useApiErrorNotice();

  const { canReadInstance, canReadPlatform, canReadTenant, canSeeIntegrity, tabs } = useAuditTabs(
    can,
    Boolean(selectedInstance),
  );

  const tenantInstances = useMemo(
    () =>
      (me?.instances ?? [])
        .filter((instance) => instance.tenantId === selectedInstance?.tenantId)
        .map((instance) => ({ id: instance.id, name: instance.name })),
    [me, selectedInstance],
  );

  // The scope is REQUESTED by the tab strip and then clamped to what the user may
  // read. Seeding the state from a permission instead would freeze whatever was
  // true on the first render — before `/api/v2/me` answers, no permission is
  // held, so every reader would have started on the platform trail and asked for
  // a route they cannot reach.
  const [requestedTab, setTab] = useState<AuditTab>("instance");
  const onTenantTab = requestedTab === "tenant" && canReadTenant;
  const onIntegrityTab = requestedTab === "integrity" && canSeeIntegrity;
  const scope = clampedScope(requestedTab, canReadInstance, canReadPlatform);
  const [view, setView] = useState<AuditView>("all");
  const [filters, setFilters] = useState<AuditFilterValues>({});
  const [selected, setSelected] = useState<AuditGridRow | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const cursor = useCursorStack();

  const isReport = view !== "all";
  // Gated on the SELECTED INSTANCE, not merely on `/api/v2/me` having answered.
  // An instance-scoped route reads `x-instance-id`, and the getter behind it has
  // no instance to give until the choice exists; a query fired any earlier goes
  // out with no header at all, which the server refuses as a validation failure.
  //
  // The PLATFORM trail is the other way round: its rows live in the platform
  // database, its route reads no instance header, and a platform administrator
  // looking at it may have chosen no instance at all — on a fresh deployment
  // there are none to choose. Waiting for one there would leave the screen
  // loading forever (FR-20, FR-70).
  const ready = isResolved && (scope === "platform" || Boolean(selectedInstance));
  // Only one of the two ever runs: they answer the same grid, and firing both
  // would audit a report read the user never asked for (SEC-16).
  const events = useAuditEvents(
    scope,
    filters,
    cursor.cursor,
    ready && !isReport && !onTenantTab && !onIntegrityTab,
  );
  const report = useAuditReport(
    scope,
    // Null, not a stand-in report id: a disabled query still reads its cache key,
    // and borrowing a real report's key would serve its rows to the trail view.
    isReport ? (view as AuditReportId) : null,
    filters,
    cursor.cursor,
    ready && isReport && !onTenantTab && !onIntegrityTab,
  );

  const rows = useMemo<AuditGridRow[]>(
    () => (isReport ? report.rows.map(reportRowToRow) : events.rows.map(eventToRow)),
    [isReport, report.rows, events.rows],
  );

  const active = isReport ? report : events;
  // Walk W19: the free-form trail's first page with no filter set and no row is a trail where
  // nothing has happened yet, not a filter that matched nothing.
  const anyFilter = Object.values(filters).some((value) => value !== undefined && value !== "");
  const trulyEmpty = rows.length === 0 && !isReport && !anyFilter && !cursor.canGoBack;
  // What an empty grid says: nothing has happened yet, or nothing matches the filters.
  const emptyMessage = trulyEmpty ? NOTHING_YET[scope] : NO_MATCHING_EVENTS;
  // The two trails are gated differently server-side: the instance export needs
  // `instance.audit.export`, the platform export only `platform.audit.read`
  // (docs/rbac.md §routes). Asking for the instance key on the platform tab would
  // hide the export from a platform administrator who has chosen no instance and
  // therefore holds no instance permission at all.
  const canExport = scope === "platform" ? canReadPlatform : can("instance.audit.export");
  const { exportCsv, isExporting } = useAuditExport();

  if (!isResolved) return <LoadingRows rows={6} columns={6} />;
  if (!canAny("instance.audit.read", "platform.audit.read", "tenant.audit.read")) {
    return <PermissionNotice permission="instance.audit.read" />;
  }

  /** Every filter change starts a new walk; keeping the cursor would page the old query. */
  function applyFilters(next: AuditFilterValues) {
    setFilters(next);
    cursor.reset();
  }

  async function handleExport() {
    try {
      // The actor travels as a header, not in the URL: see `use-audit.ts`.
      const filename = await exportCsv(auditExportPath(scope, view, filters), filters.actor);
      // The notice is the one announcement (ux-s10 §3); the page's own live line is kept for
      // the reference copy, which has no notice.
      notice.success(`${filename}. ${rows.length} events on this page.`, {
        title: "CSV downloaded",
      });
    } catch (cause) {
      showError(cause, "The export could not be produced.");
    }
  }

  function handleCopyReference(reference: string) {
    void navigator.clipboard?.writeText(reference).then(
      () => setAnnouncement(`Reference id ${reference} copied.`),
      () => setAnnouncement("The reference id could not be copied. Select it and copy manually."),
    );
  }

  const tabStrip = (
    <AuditTabStrip
      tabs={tabs}
      selectedValue={shownTab(onIntegrityTab, onTenantTab, scope)}
      onSelect={(value) => {
        setTab(value);
        setSelected(null);
        cursor.reset();
      }}
    />
  );

  // One tree for every tab, the strip at the same place in it, so choosing a tab never
  // remounts the strip and focus stays on the tab the user chose.
  const tenantTrail = onTenantTab ? tenantTrailFor(selectedInstance, tenantInstances) : null;

  return (
    <div className="space-y-4">
      <ExportCsvButton
        shown={canExport && !tenantTrail && !onIntegrityTab}
        exporting={isExporting}
        onExport={() => void handleExport()}
      />

      {tabStrip}

      {onIntegrityTab ? <AuditIntegrityPanel /> : null}

      {onIntegrityTab
        ? null
        : (tenantTrail ?? (
            <>
              <TrailControls
                scope={scope}
                canReadInstance={canReadInstance}
                view={view}
                spec={report.spec}
                onViewChange={(next) => {
                  setView(next);
                  setSelected(null);
                  cursor.reset();
                }}
                filters={filters}
                onFiltersChange={applyFilters}
              />

              <AuditResults
                active={active}
                rows={rows}
                selected={selected}
                onOpen={setSelected}
                emptyMessage={emptyMessage}
                pageLine={auditPageLine(rows.length, trulyEmpty)}
                canGoBack={cursor.canGoBack}
                onPrevious={cursor.back}
                onNext={() => active.nextCursor && cursor.next(active.nextCursor)}
                onRetry={() => {
                  if (isReport) void report.refetch();
                  else void events.refetch();
                }}
              />

              {/* The reference copy announces into a polite live region. The export's confirmation,
          with the count, is the page notice under the title (Session 10). */}
              <p
                role="status"
                aria-live="polite"
                className="sr-only"
                data-testid="audit-live-region"
              >
                {announcement}
              </p>

              <AuditEventDrawer
                row={selected}
                onOpenChange={(open) => {
                  if (!open) setSelected(null);
                }}
                onCopyReference={handleCopyReference}
              />
            </>
          ))}
    </div>
  );
}
